// Import utils
import { getChild, getChildren, getDocumentElement, getDocumentElements, updateFieldTotal } from '../../Utils/Utils';

// Import calcs
import { AllDamVolumeCalc } from './AllDamVolumeCalc';

// Global tracking variables
let FieldsTracker = {};
let DamTypes = [];
let DamType = '';

/**
 * Callback function to execute when mutations are observed. This will
 * only fire the calculation if we notice a change in the childlist nodes.
 * 
 * @param       {array}     mutationList
 * 
 * @returns     {void}
 */
const DamVolumeCalcChildNodeObserver = (mutationList) => {
    mutationList.forEach(mutation => {
        if (mutation.type === "childList") {
            DamVolumeCalc(true);
        }
    });
};

/**
 * Set the childlist mutation observer.
 * 
 * @returns     {void}
 */
const SetDamVolumeCalcMutationObserver = () => {
    const Accordion = getDocumentElement('#dam-volume');
    const ObserverConfig = { childList: true, subtree: true };

    const Observer = new MutationObserver(DamVolumeCalcChildNodeObserver);
    Observer.observe(Accordion, ObserverConfig);
};

/**
 * Reset the disabled fields in the current dam volume accordion.
 * 
 * @param       {HTMLElement}   parent
 * 
 * @returns     {void}
 */
const ResetDisabledFields = (parent) => {
    const CurrentlyDisabledFields = getChildren(parent, '[disabled="true"]');

    if (CurrentlyDisabledFields) {
        CurrentlyDisabledFields.forEach(field => {
            field.removeAttribute('disabled');
        });
    }
};

/**
 * Clear any validation errors if they are present.
 * 
 * @param       {HTMLElement}   parent
 * 
 * @returns     {void}
 */
const ClearValidationErrors = (parent) => {
    const ErrorField = getChild(parent, '.farm-calc-accordion__error-msg');
    if (ErrorField) {
        ErrorField.innerHTML = ''
    }
}

/**
 * Update certain fields depending on the selection of the dam type.
 * 
 * @returns     {void}
 */
const WatchDamTypeSelection = () => {
    const {
        DamTypeFields,
        TopRadiusFields,
        TopLengthFields,
        TopWidthFields
    } = FieldsTracker;

    DamTypes = [
        {
            name: 'circular',
            disableFields: [TopLengthFields, TopWidthFields]
        },
        {
            name: 'rectangleSquare',
            disableFields: [TopRadiusFields]
        },
        {
            name: 'gully',
            disableFields: [TopRadiusFields]
        }
    ];

    // When we switch between dam types, we want to set reset/set disabled fields.
    DamTypeFields.forEach((damTypeField, i) => {
        damTypeField.addEventListener('change', e => {
            e.stopImmediatePropagation();
            const Config = DamTypes.find(item => item.name === damTypeField.value);
            DamType = damTypeField.value;

            // Reset previously disabled fields
            ResetDisabledFields(damTypeField.closest('.farm-calc-accordion__container'));
            
            // Clear any previous validation errors if they exist
            ClearValidationErrors(damTypeField.closest('.farm-calc-accordion__container'));

            if (Config && Config.disableFields) {
                Config.disableFields.forEach(disableFields => {
                    disableFields[i].value = '';
                    disableFields[i].setAttribute('disabled', true);
                });
            }
        });
    });
};

/**
 * Update the dam water totals. If there is another field in the calculator
 * that's under a different section, update that field too.
 * 
 * @param       {array}         allFieldInstances 
 * @param       {HTMLElement}   totalsField 
 * @param       {HTMLElement}   otherField
 * 
 * @returns     {void}
 */
const updateSingleTotals = (allFieldInstances, totalsField, otherField) => {
    let result = 0;

    allFieldInstances.forEach(field => {
        const value = field instanceof HTMLElement ? field.valueAsNumber : null;

        if (value !== null) {
            result += value;
        }
    });

    // console.log(result.toFixed(3));
    totalsField.value = result.toFixed(3);

    if (otherField) {
        otherField.value = result.toFixed(3);
    }
};

/**
 * Setup the update for the dam water totals.
 * 
 * @returns     {void}
 */
const UpdateTotals = () => {
    const SurfaceAreaTotal = getDocumentElement('#surfaceAreaTotal');
    const DamVolumeTotal = getDocumentElement('#damVolumeTotal');
    const EvapLossesTotal = getDocumentElement('#evapTotal');
    const SeepageLossesTotal = getDocumentElement('#seepageTotal');
    const RainOnDamTotal = getDocumentElement('#rainTotal');
    const DamSummaryTotal = getDocumentElement('#tot_dam_volume');
    const WaterUseEvap = getDocumentElement('.farm-calc-section--use #tot_evaporation');
    const WaterUseSeepage = getDocumentElement('.farm-calc-section--use #tot_seepage');
    const SupplyRainOnDam = getDocumentElement('.farm-calc-section--supply #tot_dam');

    if (SurfaceAreaTotal &&
        DamVolumeTotal &&
        EvapLossesTotal &&
        SeepageLossesTotal &&
        RainOnDamTotal &&
        DamSummaryTotal
    ) {
        const SurfaceAreaFields = getDocumentElements('[id^="dam_surface-"]');
        const DamVolumeFields = getDocumentElements('[id^="dam_volume-"]');
        const EvapLossesFields = getDocumentElements('[id^="dam_evap-"]');
        const SeepageLossesFields = getDocumentElements('[id^="dam_see-"]');
        const RainOnDamFields = getDocumentElements('[id^="dam_rain-"]');

        // Update the Dam water totals.
        updateSingleTotals(SurfaceAreaFields, SurfaceAreaTotal);
        updateSingleTotals(DamVolumeFields, DamVolumeTotal);
        updateSingleTotals(EvapLossesFields, EvapLossesTotal, WaterUseEvap);
        updateSingleTotals(SeepageLossesFields, SeepageLossesTotal, WaterUseSeepage);
        updateSingleTotals(RainOnDamFields, RainOnDamTotal, SupplyRainOnDam);

        // Update total dam volume total
        updateFieldTotal(DamVolumeFields, DamVolumeTotal, DamSummaryTotal);
    }
};

/**
 * Reupdate totals when a clone is removed
 */
document.addEventListener('clonable:removed', e => {
    if (e.detail.id === 'dam') {
        UpdateTotals();
    }
});

/**
 * Set the watch events for the fields. This also handles calling
 * the dam volume calculations.
 * 
 * @returns     {void}
 */
const SetFieldWatchers = () => {
    const watchFieldCollection = Object.values(FieldsTracker);

    watchFieldCollection.forEach(watchFields => {
        watchFields.forEach(field => {
            if (field.tagName !== 'SELECT') {
                field.addEventListener('input', (e) => {
                    e.stopImmediatePropagation();

                    AllDamVolumeCalc({
                        accordionNumber: field.closest('.farm-calc-accordion--nested').dataset.number,
                        type: DamType,
                        selectors: {
                            depthField: '#dam_depth',
                            radiusField: '#dam_radius',
                            surfaceAreaField: '#dam_surface',
                            lengthField: '#dam_length',
                            widthField: '#dam_width',
                            annualEvaporationField: '#evaporation',
                            damEvaporationField: '#dam_evap',
                            rainSurfaceField: '#dam_rain',
                            annualRainfallField: '#rainfall',
                            damVolumeField: '#dam_volume',
                            seepageField: '#dam_see',
                        }
                    }).init();

                    UpdateTotals();
                });
            }
        });
    });
};

/**
 * Watch for summary field changes so we can update the overall
 * farm water storage.
 * 
 * @returns     {void}
 */
const WatchSummary = () => {
    const TotalDamVolume = getDocumentElement('#tot_dam_volume');
    const TotalTankVolume = getDocumentElement('#tot_tanks_volume');
    const TotalFarmWaterStorage = getDocumentElement('#waterStorageTotal');

    setInterval(() => {
        let calc = TotalDamVolume.valueAsNumber + TotalTankVolume.valueAsNumber;
        TotalFarmWaterStorage.value = calc.toFixed(3);
    }, 200);
};

/**
 * If the component has already been mounted, we don't want to setup
 * the MutationObserver. This file sets up the Dam volume accordion.
 * Calculations are run in a separate file.
 * 
 * @param       {boolean}       mounted
 * 
 * @returns     {void}
 */
export const DamVolumeCalc = (mounted) => {
    const DamTypeFields = getDocumentElements('#dam-volume [id^="dam_type-"]');
    const DepthFields = getDocumentElements('#dam-volume [id^="dam_depth-"]');
    const TopRadiusFields = getDocumentElements('#dam-volume [id^="dam_radius-"]');
    const TopLengthFields = getDocumentElements('#dam-volume [id^="dam_length-"]');
    const TopWidthFields = getDocumentElements('#dam-volume [id^="dam_width-"]');
    const SurfaceAreaFields = getDocumentElements('#dam-volume [id^="dam_surface-"]');

    if (DamTypeFields &&
        TopRadiusFields &&
        TopLengthFields &&
        TopWidthFields &&
        SurfaceAreaFields &&
        DepthFields
    ) {
        FieldsTracker = {
            DamTypeFields,
            TopRadiusFields,
            TopLengthFields,
            TopWidthFields,
            DepthFields
        };
        // SurfaceAreaFields,
        if (mounted === false) {
            SetDamVolumeCalcMutationObserver();
        }

        // Setup all fields
        WatchDamTypeSelection();
        SetFieldWatchers();
        WatchSummary();
    } else {
        console.log('Missing dam fields');
    }
};
